import { Injectable, computed, signal } from '@angular/core';
import type {
  Notification,
  NotificationSummary,
  NotificationFilter,
  NotificationGrouping,
  AddNotificationRequest,
  GetNotificationsRequest,
  GetNotificationsResponse,
  NotificationResponse
} from '../models/notification.model';
import { NotificationCategory, NotificationPriority, NotificationType } from '../models/notification.model';
import type { INotificationsStore } from './notifications-store.interface';

@Injectable({ providedIn: 'root' })
export class NotificationsMockStore implements INotificationsStore {
  private readonly _notifications = signal<readonly Notification[]>([]);
  private readonly _summary = signal<NotificationSummary | null>(null);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly notifications = this._notifications.asReadonly();
  readonly summary = this._summary.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._notifications().length);
  readonly unread = computed(() => this._notifications().filter(n => !n.read));
  readonly urgent = computed(() =>
    this._notifications().filter(n => n.priority === NotificationPriority.URGENT && !n.read)
  );
  readonly unreadCount = computed(() => this.unread().length);
  readonly urgentCount = computed(() => this.urgent().length);

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  async addNotification(request: AddNotificationRequest): Promise<NotificationResponse> {
    const id = `mock-notif-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const n: Notification = {
      notificationId: id,
      type: request.type,
      category: request.category,
      title: request.title,
      message: request.message,
      data: request.data,
      priority: request.priority ?? NotificationPriority.NORMAL,
      actionUrl: request.actionUrl,
      actionLabel: request.actionLabel,
      icon: request.icon,
      read: false,
      createdAt: new Date()
    };
    this._notifications.set([n, ...this._notifications()]);
    return { success: true, message: 'Notification added', notificationId: id };
  }

  async markAsRead(notificationId: string): Promise<NotificationResponse> {
    this._notifications.set(
      this._notifications().map(n =>
        n.notificationId === notificationId ? { ...n, read: true, readAt: new Date() } : n
      )
    );
    return { success: true, message: 'Marked as read' };
  }

  async markAllAsRead(): Promise<NotificationResponse> {
    const now = new Date();
    this._notifications.set(this._notifications().map(n => ({ ...n, read: true, readAt: now })));
    return { success: true, message: 'All marked as read' };
  }

  async remove(notificationId: string): Promise<NotificationResponse> {
    this._notifications.set(this._notifications().filter(n => n.notificationId !== notificationId));
    return { success: true, message: 'Removed' };
  }

  async fetch(_request: GetNotificationsRequest = {}): Promise<GetNotificationsResponse> {
    const notifications = [...this._notifications()];
    return {
      success: true,
      notifications,
      totalCount: notifications.length,
      unreadCount: notifications.filter(n => !n.read).length,
      filteredCount: notifications.length,
      hasMore: false
    };
  }

  async cleanupExpired(): Promise<NotificationResponse> {
    const now = Date.now();
    this._notifications.set(
      this._notifications().filter(n => !n.expiresAt || new Date(n.expiresAt).getTime() > now)
    );
    return { success: true, message: 'Cleaned up' };
  }

  async createTestNotifications(count = 1): Promise<NotificationResponse> {
    for (let i = 0; i < count; i++) {
      await this.addNotification({
        type: NotificationType.TEST,
        category: NotificationCategory.SYSTEM,
        title: `Mock test notification ${i + 1}`,
        message: 'Generated by MockStore.createTestNotifications',
        priority: NotificationPriority.NORMAL
      });
    }
    return { success: true, message: `Created ${count} test notifications` };
  }

  filter(filter: NotificationFilter): readonly Notification[] {
    return this._notifications().filter(n => {
      if (filter.unreadOnly && n.read) return false;
      if (filter.priority && n.priority !== filter.priority) return false;
      if (filter.category && n.category !== filter.category) return false;
      if (filter.search) {
        const q = filter.search.toLowerCase();
        const hay = `${n.title} ${n.message}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  grouped(): NotificationGrouping {
    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    const startOfThisWeek = new Date(startOfToday);
    startOfThisWeek.setDate(startOfThisWeek.getDate() - 7);

    const groups: NotificationGrouping = { today: [], yesterday: [], thisWeek: [], older: [] };
    for (const n of this._notifications()) {
      const d = new Date(n.createdAt);
      if (d >= startOfToday) groups.today.push(n);
      else if (d >= startOfYesterday) groups.yesterday.push(n);
      else if (d >= startOfThisWeek) groups.thisWeek.push(n);
      else groups.older.push(n);
    }
    return groups;
  }
}
